// Performance utilities inspired by Skiper UI
export class PerformanceManager {
  private static instance: PerformanceManager
  private observers: Map<string, PerformanceObserver> = new Map()
  private timers: Map<string, number> = new Map()

  static getInstance(): PerformanceManager {
    if (!PerformanceManager.instance) {
      PerformanceManager.instance = new PerformanceManager()
    }
    return PerformanceManager.instance
  }

  // Monitor theme transition performance
  startThemeTransition(): void {
    this.timers.set('theme-transition', performance.now())
    
    // Add performance hints
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        this.optimizeForThemeTransition()
      })
    }
  }

  endThemeTransition(): void {
    const startTime = this.timers.get('theme-transition')
    if (startTime) {
      const duration = performance.now() - startTime
      console.debug(`Theme transition completed in ${duration.toFixed(2)}ms`)
      this.timers.delete('theme-transition')
      
      // Clean up performance optimizations
      this.cleanupThemeTransition()
    }
  }

  private optimizeForThemeTransition(): void {
    // Temporarily reduce animation complexity
    document.documentElement.style.setProperty('--transition-fast', '100ms')
    document.documentElement.style.setProperty('--transition-normal', '150ms')
  }

  private cleanupThemeTransition(): void {
    // Restore normal transition speeds
    setTimeout(() => {
      document.documentElement.style.removeProperty('--transition-fast')
      document.documentElement.style.removeProperty('--transition-normal')
    }, 100)
  }

  // Memory cleanup utility
  cleanup(): void {
    this.observers.forEach(observer => observer.disconnect())
    this.observers.clear()
    this.timers.clear()
  }

  // Optimize scroll performance
  optimizeScrolling(element: HTMLElement): void {
    element.style.willChange = 'scroll-position'
    element.style.contain = 'layout style paint'
    
    // Clean up after scroll ends
    let scrollTimeout: NodeJS.Timeout
    element.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(() => {
        element.style.willChange = 'auto'
        element.style.contain = 'none'
      }, 150)
    }, { passive: true })
  }

  // Debounce utility for performance
  debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
  ): (...args: Parameters<T>) => void {
    let timeout: NodeJS.Timeout
    return (...args: Parameters<T>) => {
      clearTimeout(timeout)
      timeout = setTimeout(() => func.apply(this, args), wait)
    }
  }

  // Throttle utility for performance
  throttle<T extends (...args: any[]) => any>(
    func: T,
    limit: number
  ): (...args: Parameters<T>) => void {
    let inThrottle: boolean
    return (...args: Parameters<T>) => {
      if (!inThrottle) {
        func.apply(this, args)
        inThrottle = true
        setTimeout(() => inThrottle = false, limit)
      }
    }
  }
}

// Export singleton instance
export const performanceManager = PerformanceManager.getInstance()

// Cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    performanceManager.cleanup()
  })
}